import React, { useRef } from 'react';

import { Calendar } from 'lucide-react';

import { fieldFrame } from './fieldFrame';
import { cn } from './utils';


/**
 * Text input matching the DESIGN.md `input` token:
 * surface-1 bg, hairline-strong border, focus 1px primary + soft glow.
 * Supports optional leading/trailing adornments.
 *
 * For `type="date"` it owns date behavior app-wide: a single trailing calendar
 * button opens the native picker via `showPicker()` (the duplicate native
 * indicator is hidden in global.css), so form fields and inline date inputs
 * behave identically.
 */
const Input = React.forwardRef((props, ref) => {
  const {
    className,
    type = 'text',
    invalid = false,
    leading = null,
    trailing = null,
    ...restProps
  } = props;

  const inputRef = useRef(null);

  const setRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    }
    else if (ref) {
      ref.current = node;
    }
  };

  const openPicker = () => {
    const el = inputRef.current;
    if (el && typeof el.showPicker === 'function') {
      try {
        el.showPicker();
      }
      catch {
        el.focus();
      }
    }
    else {
      el?.focus();
    }
  };

  const isDate = type === 'date';

  const resolvedTrailing = trailing ?? (isDate
    ? (
      <button
        type="button"
        aria-label="Open date picker"
        disabled={restProps.disabled}
        className={cn(
          'flex items-center text-ink-subtle outline-none transition-colors',
          'hover:text-ink focus-visible:text-ink disabled:cursor-not-allowed',
        )}
        onClick={openPicker}
      >
        <Calendar />
      </button>
    )
    : null);

  const adorned = Boolean(leading || resolvedTrailing);

  const input = (
    <input
      ref={setRef}
      type={type}
      className={cn(
        fieldFrame({ focus: 'visible', invalid }),
        'h-10 w-full px-3 text-sm text-ink',
        'placeholder:text-ink-subtle outline-none',
        'disabled:cursor-not-allowed disabled:opacity-45',
        adorned && 'border-0 focus-visible:ring-0 bg-transparent px-0 h-auto',
        !adorned && className,
      )}
      {...restProps}
    />
  );

  if (!adorned) {
    return input;
  }

  return (
    <div
      className={cn(
        fieldFrame({ focus: 'within', invalid }),
        'flex h-10 w-full items-center gap-2 px-3',
        className,
      )}
    >
      {leading
        ? (
          <span className="flex shrink-0 items-center text-ink-subtle [&_svg]:size-4">{leading}</span>
        )
        : null}

      {input}

      {resolvedTrailing
        ? (
          <span className="flex shrink-0 items-center text-ink-subtle [&_svg]:size-4">{resolvedTrailing}</span>
        )
        : null}
    </div>
  );
});

Input.displayName = 'Input';

export { Input };
export default Input;
